import { createSelector } from 'reselect';
import { redirect } from 'redux-first-router';
import { fromJS } from 'immutable';
import { getCurrentInstitution } from './routeThunks/institution/getCurrentInstitution';
import { reservedPrefixes } from './reservedPrefixes';
import { RESERVED_COURSE_PATHS } from './constants';
import {
  CUSTOM_DOMAINS,
  MAIN_DOMAIN,
  MAIN_DOMAIN_OPTIONS,
  STAY_ON_ANY_DOMAIN,
} from './routesHelpers';
import {
  PROFILE_PAGE_ABOUT,
  PROFILE_PAGE_PORTFOLIO,
  PROFILE_PAGE_COMMUNITIES,
  PROFILE_PAGE_CREDENTIALS,
} from '../web/containers/ProfilePage/consts';

import {
  ROUTE_INSTITUTION_ADMIN,
  ROUTE_INSTITUTION_HOMEPAGE,
  ROUTE_INSTITUTION_PAGE,
  ROUTE_INSTITUTION_QUALIFICATION_PAGE,
  ROUTE_INSTITUTION_SUBSCRIPTIONS_PAGE,
  ROUTE_HOMEPAGE,
  ROUTE_MARKETING_PAGE,
  ROUTE_CONTACT_PAGE,
  ROUTE_USER_PROFILE_PAGE,
  ROUTE_USER_PROFILE_PAGE_BLOG,
  ROUTE_ACCOUNTS_LOGIN,
  ROUTE_ACCOUNTS_SIGNUP,
  ROUTE_ACCOUNTS_SUBSCRIPTIONS,
  ROUTE_ACCOUNTS_UNKNOWN,
  ROUTE_OTHER_PAGE,
  ROUTE_COURSES,
  ROUTE_COURSE_SPECIAL_PAGE,
  ROUTE_COURSE_ADMIN,
  ROUTE_COHORT_ADMIN,
  ROUTE_COURSE_ASSESSMENT,
  ROUTE_COURSE_CERTIFICATION,
  ROUTE_COURSE_PROGRESS,
  ROUTE_COURSE_PAGE,
  ROUTE_WASABI_HOME,
  ROUTE_WASABI_COURSE_LIST,
  ROUTE_WASABI_COURSE_INFO,
  ROUTE_WASABI_INSTITUTION_LIST,
  ROUTE_WASABI_INSTITUTION_INFO,
  ROUTE_WASABI_USER_LIST,
  ROUTE_WASABI_USER_INFO,
  ROUTE_WASABI_EMAIL_TEMPLATES,
  ROUTE_WASABI_PAYMENTS,
  ROUTE_WASABI_SUBSCRIPTIONS,
  ROUTE_WASABI_SAAS,
  ROUTE_SSO,
  ROUTE_PAYMENT_DETAILS_PAGE,
  ROUTE_PAYMENT_THANKYOU_PAGE,
  ROUTE_PAYMENT_OTHER_PAGE,
  ROUTE_MY_COURSES_PAGE,
  ROUTE_CREATE_COURSE_PAGE,
  ROUTE_BLOCKPAGE_CONTENT,
  ROUTE_CERTIFICATE_VERIFICATION_PAGE,
  ROUTE_WASABI_PORTFOLIO_INSTITUTIONS,
  ROUTE_ACCOUNTS_SETTINGS,
  ROUTE_SSR_TEST,
  ROUTE_ACCOUNTS_NOTIFICATIONS,
  ROUTE_PAYMENT_UPDATE_PAYMENT_METHOD_PAGE,
} from './routeActions';
import {
  makeSelectCoursePathUsesCustomDomain,
  makeSelectOriginForCoursePath,
} from './Courses/CustomDomains/helpers';
import { selectCurrentlyViewProfilePageIsDirty } from './ProfilePage/selectors';
import { makeSelectCustomOriginFromInstitutionPath } from './Institutions/helpers';
import { UPDATE_PROFILE_PAGE_SET_WARNING_CALLBACK } from './ProfilePage/actions';

/* global _OL_ORIGIN */

// Docs:
// Simple cases: https://github.com/faceyspacey/redux-first-router/blob/master/docs/connectRoutes.md#routesmap
// complex cases: https://github.com/faceyspacey/redux-first-router/issues/83#issuecomment-327703226

/**
 * Custom domain selector creators:
 * helper functions to create selectors from a routing action
 * The returned selector selects from the redux state one of two things:
 *  - whether the route can be rendered on a type of domain (main or custom)
 *  - for custom domains, the particular origin of the custom domain.
 *
 * Note: If a route renders links, The data needed by the below selectors
 * (i.e. the instututions linked to) must be loaded in the route thunk!
 */

const queryCourse = (action) => action.query.course;
const queryInstitution = (action) => action.query.institution;

const makeSelectCustomOriginFromQueryCourse = (action) => {
  const coursePath = queryCourse(action);
  return createSelector(
    [
      makeSelectCoursePathUsesCustomDomain(coursePath),
      makeSelectOriginForCoursePath(coursePath),
    ],
    (courseUsesCustomDomain, originForCoursePath) =>
      courseUsesCustomDomain && originForCoursePath
  );
};

const makeSelectCustomOriginFromQueryInstitution = (action) =>
  makeSelectCustomOriginFromInstitutionPath(queryInstitution(action));

const makeSelectCustomOriginFromInstitutionPrefix = (action) =>
  makeSelectCustomOriginFromInstitutionPath(
    (action.state || {}).institutionPrefix
  );

const makeSelectOlOriginForInstitutionPrefix = (action) =>
  createSelector(
    [
      makeSelectCustomOriginFromInstitutionPath(
        (action.state || {}).institutionPrefix
      ),
    ],
    (customOrigin) => (customOrigin ? null : _OL_ORIGIN)
  );

const loadWasabiThunk = (request) =>
  import(`./routeThunks/wasabi/loadWasabi`).then(({ loadWasabi }) =>
    loadWasabi(request)
  );

/**
 * Please extend this so that it includes all the routes on the entire site :)
 *
 * If you go to one of these urls, the corresponding action will be dispatched.
 * <-->
 * If you dispatch one of these actions, the corresponding url will be pushed
 *
 * You can use the thunk option to retrieve data necessary for a route (it's
 * probably also a good idea to use composition to fetch more specific data
 * for specific pages, e.g. retrieveDataForAccountSettings might itself call
 * retrieveDataForUserAccountPages). Additionally:
 *  1.  if you provide a `beforeThunk` in a domain option, it will be
 *      combined with the main thunk like `beforeThunk.then(() => thunk())`
 *      allowing the route to depend on data based on the domain.
 *  2.  if you provide a `duringThunk` in a domain options, it will be
 *      combined with the main thunk like `Promise.all([thunk, duringThunk])`
 *      allowing the route the domains data and the routes data to be
 *      fetched in parallel.
 *
 * This is transformed by makeRoutesMapForOrigin() into a routesMap compatible
 * with redux-first-router, which is different depending on whether we are on
 * the main domain or a custom domain.
 *
 * Each route has a key for the main domain and a key for the custom domain,
 * supporting all the options of redux-first-router. When a route action is
 * dispatched, a domain is chosen as follows:
 *  1. if "stickyDomain" is set on the route, the current domain is chosen if
 *     its makeSelectOriginForAction(action)(state) returns a truthy value for the action.
 *     The default makeSelectOriginForAction for the main domain returns the main domain.
 *  2. The first domain defined in the map for which makeSelectOriginForAction(action)(State)
 *     returns a truthy value. In this case, that origin is used.
 */
export const multiDomainRoutesMap = {
  // Homepage and others
  [ROUTE_HOMEPAGE]: {
    path: '/',
  },

  // Marketing pages (mostly linked from footer)
  [ROUTE_MARKETING_PAGE]: {
    path: `/:page(${fromJS(['learningservices', 'about']).join('|')})/`,
  },

  [ROUTE_CONTACT_PAGE]: {
    path: `/contact/`,
  },

  // Wasabi Admin/CMS
  [ROUTE_WASABI_COURSE_INFO]: {
    path: '/wasabi/courses/:courseId/',
    thunk: loadWasabiThunk,
  },
  [ROUTE_WASABI_COURSE_LIST]: {
    path: '/wasabi/courses/',
    thunk: loadWasabiThunk,
  },
  [ROUTE_WASABI_INSTITUTION_INFO]: {
    path: '/wasabi/institutions/:institutionId/:tabName*/',
    thunk: (request) =>
      Promise.all([
        loadWasabiThunk(request),
        import(`./routeThunks/wasabi/loadWasabiInstitutionDetails`).then(
          ({ loadWasabiInstitutionDetails }) =>
            loadWasabiInstitutionDetails(request)
        ),
      ]),
  },
  [ROUTE_WASABI_INSTITUTION_LIST]: {
    path: '/wasabi/institutions/',
    thunk: (request) =>
      Promise.all([
        loadWasabiThunk(request),
        import(`./routeThunks/wasabi/loadWasabiInstitutionsData`).then(
          ({ loadWasabiInstitutionsData }) =>
            loadWasabiInstitutionsData(request)
        ),
      ]),
  },
  [ROUTE_WASABI_USER_INFO]: {
    path: '/wasabi/users/:userId/:tabName*/',
    thunk: (request) =>
      Promise.all([
        loadWasabiThunk(request),
        import(`./routeThunks/wasabi/loadWasabiUserDetails`).then(
          ({ loadWasabiUserDetails }) => loadWasabiUserDetails(request)
        ),
      ]),
  },
  [ROUTE_WASABI_USER_LIST]: {
    path: '/wasabi/users/',
    thunk: (request) =>
      Promise.all([
        loadWasabiThunk(request),
        import(`./routeThunks/wasabi/loadWasabiUsersData`).then(
          ({ loadWasabiUsersData }) => loadWasabiUsersData(request)
        ),
      ]),
  },
  [ROUTE_WASABI_EMAIL_TEMPLATES]: {
    path: '/wasabi/email_templates/',
    thunk: (request) =>
      Promise.all([
        loadWasabiThunk(request),
        import(`./routeThunks/wasabi/loadWasabiEmailTemplates`).then(
          ({ loadWasabiEmailTemplates }) => loadWasabiEmailTemplates(request)
        ),
      ]),
  },
  [ROUTE_WASABI_PAYMENTS]: {
    path: '/wasabi/payments/',
    thunk: loadWasabiThunk,
  },
  [ROUTE_WASABI_SUBSCRIPTIONS]: {
    path: '/wasabi/subscriptions/',
    thunk: loadWasabiThunk,
  },
  [ROUTE_WASABI_PORTFOLIO_INSTITUTIONS]: {
    path: '/wasabi/portfolio-institutions/',
    thunk: (request) => {
      return Promise.all([
        loadWasabiThunk(request),
        import(`./routeThunks/wasabi/loadWasabiInstitutionPortfolioData`).then(
          ({ loadWasabiInstitutionPortfolioData }) =>
            loadWasabiInstitutionPortfolioData(request)
        ),
      ]);
    },
  },
  [ROUTE_WASABI_SAAS]: {
    path: '/wasabi/saas/:tabName*/',
    thunk: (request) =>
      Promise.all([
        loadWasabiThunk(request),
        import(`./routeThunks/wasabi/loadWasabiSaasData`).then(
          ({ loadWasabiSaasData }) => loadWasabiSaasData(request)
        ),
      ]),
  },
  [ROUTE_WASABI_HOME]: {
    path: '/wasabi/',
    thunk: (request) => {
      const { dispatch } = request;
      return dispatch(
        redirect({
          type: ROUTE_WASABI_SAAS,
        })
      );
    },
  },

  [ROUTE_SSO]: {
    ...STAY_ON_ANY_DOMAIN,
    path: `/sso/:action(${fromJS(['login', 'fail', 'complete', 'confirm']).join(
      '|'
    )})*/`,
    thunk: (request) =>
      import(`./routeThunks/sso/loadSsoPageData`).then(({ onLoadSSOPage }) =>
        onLoadSSOPage(request)
      ),
  },

  // TODO: merge this route with below when the user blog is rendered by the
  // profile page react components
  [ROUTE_USER_PROFILE_PAGE_BLOG]: {
    ...STAY_ON_ANY_DOMAIN,
    path: '/u/:profileName/blog/',
  },
  // User profile
  [ROUTE_USER_PROFILE_PAGE]: {
    ...STAY_ON_ANY_DOMAIN,
    path: `/u/:profileName/:page(${fromJS([
      PROFILE_PAGE_ABOUT,
      PROFILE_PAGE_PORTFOLIO,
      PROFILE_PAGE_COMMUNITIES,
      PROFILE_PAGE_CREDENTIALS,
    ]).join('|')})*/`,
    thunk: (request) =>
      import(`./routeThunks/profile/getCurrentProfilePageData`).then(
        ({ getCurrentProfilePageData }) => getCurrentProfilePageData(request)
      ),
    beforeLeave: ({ dispatch, getState, confirm }) => {
      if (selectCurrentlyViewProfilePageIsDirty(getState())) {
        dispatch({
          type: UPDATE_PROFILE_PAGE_SET_WARNING_CALLBACK,
          /**
           * TODO: There is no need to save this callback in redux, since
           * the rudy confirm action can be dispatched directly from the
           * component to allow or deny the transition
           */
          callback: confirm,
        });
        return false;
      }
      return true;
    },
  },

  // User accounts
  [ROUTE_ACCOUNTS_LOGIN]: {
    ...STAY_ON_ANY_DOMAIN,
    path: '/accounts/login/',
  },
  [ROUTE_ACCOUNTS_SIGNUP]: {
    path: '/accounts/register/',
  },
  [ROUTE_ACCOUNTS_SETTINGS]: {
    ...STAY_ON_ANY_DOMAIN,
    path: '/accounts/account-settings/',
  },
  [ROUTE_ACCOUNTS_NOTIFICATIONS]: {
    ...STAY_ON_ANY_DOMAIN,
    path: '/accounts/notifications/',
  },
  [ROUTE_ACCOUNTS_SUBSCRIPTIONS]: {
    ...STAY_ON_ANY_DOMAIN,
    path: '/accounts/subscriptions/',
    thunk: (request) =>
      import(`./routeThunks/accounts/loadSubscriptionPlansData`).then(
        ({ loadSubscriptionPlansData }) => loadSubscriptionPlansData(request)
      ),
  },
  [ROUTE_ACCOUNTS_UNKNOWN]: {
    ...STAY_ON_ANY_DOMAIN,
    path: '/accounts/:subPath*/',
  },

  // Related to courses but not in a course
  [ROUTE_COURSES]: {
    path: '/courses/',
  },

  // Courses
  [ROUTE_COURSE_ADMIN]: {
    stickyDomain: true,
    domains: [
      {
        domain: CUSTOM_DOMAINS,
        makeSelectOriginForAction: makeSelectCustomOriginFromQueryCourse,
        duringThunk: getCurrentInstitution,
      },
      MAIN_DOMAIN_OPTIONS,
    ],
    path: '/courses/admin/',
    thunk: (request) => {
      const { dispatch, action } = request;
      const {
        query: { action: adminAction },
      } = action;
      if (!adminAction) {
        return dispatch(
          redirect({
            ...action,
            query: {
              ...action.query,
              action: 'starter',
            },
          })
        );
      }
      return Promise.all([
        import(`./routeThunks/course/loadCourseAdminData`).then(
          ({ loadCourseAdminData }) => loadCourseAdminData(request)
        ),
        import(`./routeThunks/course/loadCourseNavigationData`).then(
          ({ loadCourseNavigationData }) => loadCourseNavigationData(request)
        ),
      ]);
    },
  },

  [ROUTE_MY_COURSES_PAGE]: {
    ...STAY_ON_ANY_DOMAIN,
    path: '/courses/my/',
  },

  [ROUTE_CREATE_COURSE_PAGE]: {
    path: '/courses/create/',
    thunk: ({ dispatch }) =>
      import(`./UserSubscriptionPlans/dispatchers`).then(
        ({ loadCurrentUserSubscriptionPlanData }) =>
          dispatch(loadCurrentUserSubscriptionPlanData)
      ),
  },

  [ROUTE_COURSE_SPECIAL_PAGE]: {
    ...STAY_ON_ANY_DOMAIN,
    path: `/courses/:courseAction(${RESERVED_COURSE_PATHS.join(
      '|'
    )})/:subPath*/`,
  },
  [ROUTE_COHORT_ADMIN]: {
    stickyDomain: true,
    domains: [
      {
        domain: CUSTOM_DOMAINS,
        makeSelectOriginForAction: makeSelectCustomOriginFromQueryCourse,
        duringThunk: getCurrentInstitution,
      },
      MAIN_DOMAIN_OPTIONS,
    ],
    path: '/cohorts/admin/',
    thunk: (request) =>
      import(`./routeThunks/course/loadCourseNavigationData`).then(
        ({ loadCourseNavigationData }) => loadCourseNavigationData(request)
      ),
  },
  [ROUTE_COURSE_ASSESSMENT]: {
    stickyDomain: true,
    path: '/assessment/:action/:designId?/:studentId?/',
    domains: [
      {
        domain: CUSTOM_DOMAINS,
        makeSelectOriginForAction: makeSelectCustomOriginFromQueryCourse,
        duringThunk: getCurrentInstitution,
      },
      MAIN_DOMAIN_OPTIONS,
    ],
    thunk: (request) =>
      import(`./routeThunks/course/loadCourseNavigationData`).then(
        ({ loadCourseNavigationData }) => loadCourseNavigationData(request)
      ),
  },
  [ROUTE_COURSE_PROGRESS]: {
    stickyDomain: true,
    path: '/progress/',
    domains: [
      {
        domain: CUSTOM_DOMAINS,
        makeSelectOriginForAction: makeSelectCustomOriginFromQueryCourse,
        duringThunk: getCurrentInstitution,
      },
      MAIN_DOMAIN_OPTIONS,
    ],
    thunk: (request) =>
      Promise.all([
        import(`./routeThunks/course/loadCourseNavigationData`).then(
          ({ loadCourseNavigationData }) => loadCourseNavigationData(request)
        ),
        import(`./routeThunks/course/loadProgressPageData`).then(
          ({ loadProgressPageData }) => loadProgressPageData(request)
        ),
      ]),
  },
  [ROUTE_COURSE_CERTIFICATION]: {
    stickyDomain: true,
    path: '/certification/:action/',
    domains: [
      {
        domain: CUSTOM_DOMAINS,
        makeSelectOriginForAction: makeSelectCustomOriginFromQueryCourse,
        duringThunk: getCurrentInstitution,
      },
      MAIN_DOMAIN_OPTIONS,
    ],
    thunk: (request) => {
      import(`./routeThunks/course/loadCourseNavigationData`).then(
        ({ loadCourseNavigationData }) => loadCourseNavigationData(request)
      );
      import(`./routeThunks/certification/loadCertificationData`).then(
        ({ loadCertificationData }) => loadCertificationData(request)
      );
    },
  },

  // User created course pages
  [ROUTE_COURSE_PAGE]: {
    domains: [
      {
        domain: CUSTOM_DOMAINS,
        path: '/courses/:courseCode/:subPath*/',
        beforeThunk: getCurrentInstitution,
        makeSelectOriginForAction: makeSelectCustomOriginFromInstitutionPrefix,
      },
      {
        domain: MAIN_DOMAIN,
        path: '/:institutionPrefix?/courses/:courseCode/:subPath*/',
        beforeThunk: getCurrentInstitution,
        makeSelectOriginForAction: makeSelectOlOriginForInstitutionPrefix,
      },
    ],
    injectInstitutionPrefix: true,
    thunk: (request) =>
      import(`./routeThunks/course/onEnterCourse`).then(
        ({ onEnterCoursesSubPath }) => onEnterCoursesSubPath(request)
      ),
  },

  // Institution admin
  [ROUTE_INSTITUTION_ADMIN]: {
    stickyDomain: true,
    domains: [
      {
        domain: CUSTOM_DOMAINS,
        makeSelectOriginForAction: makeSelectCustomOriginFromQueryInstitution,
        duringThunk: getCurrentInstitution,
      },
      MAIN_DOMAIN_OPTIONS,
    ],
    path: '/institution/admin/',
    thunk: (request) =>
      Promise.all([
        import(
          `./routeThunks/institution/getCurrentInstitutionAdminInstitution`
        ).then(({ getCurrentInstitutionAdminInstitution }) =>
          getCurrentInstitutionAdminInstitution(request)
        ),
        import(`./routeThunks/institution/loadInstitutionAdminData`).then(
          ({ loadInstitutionAdminData }) => loadInstitutionAdminData(request)
        ),
      ]),
    beforeLeave: (request) =>
      import(`./routeThunks/institution/onLeaveInstitutionAdmin`).then(
        ({ cleanUpSubscriptions }) => cleanUpSubscriptions(request)
      ),
  },

  [ROUTE_SSR_TEST]: {
    path: '/rendering/ssrtest/',
    thunk: ({ dispatch, action: { query } }) => {
      if (query.food !== 'potato') {
        dispatch(
          redirect({
            type: ROUTE_SSR_TEST,
            query: {
              food: 'potato', // Eat your cabbage and potato sog!
            },
          })
        );
      }
    },
  },
  // Endpoint for rendering block page content for app webviews
  [ROUTE_BLOCKPAGE_CONTENT]: {
    path: '/rendering/page/view-content/:pageId*/',
    // revision (id), and path should be in the query
    thunk: (request) => {
      /**
       * TODO
       *
       * Trigger any thunks needed to load the data required for
       * the blockpage to render correctly.
       *
       * Some of these may need to be untangled from react components,
       * and also added to other routes where blockpages are used,
       * e.g. `ROUTE_OTHER_PAGE`, `ROUTE_COURSE_PAGE`, etc.
       *
       * Other data may come into the redux store on web routes from
       * `<script type="application/json" />` elements on the page.
       * These should also be refactored so that they are fetched here
       * and in the thunks for the other blockpage routes.
       */
      import(`./routeThunks/pageView/loadData`).then(({ loadData }) =>
        loadData(request)
      );
    },
  },

  // Payment pages
  [ROUTE_PAYMENT_DETAILS_PAGE]: {
    ...STAY_ON_ANY_DOMAIN,
    path: '/payment/details/',
    thunk: (request) =>
      import(`./routeThunks/payment/onEnterPaymentPage`).then(
        ({ onEnterPaymentPage }) => onEnterPaymentPage(request)
      ),
  },
  [ROUTE_PAYMENT_UPDATE_PAYMENT_METHOD_PAGE]: {
    ...STAY_ON_ANY_DOMAIN,
    path: '/payment/update/',
    thunk: (request) =>
      import(`./routeThunks/payment/onEnterUpdatePaymentMethodPage`).then(
        ({ onEnterUpdatePaymentMethodPage }) =>
          onEnterUpdatePaymentMethodPage(request)
      ),
  },
  [ROUTE_PAYMENT_THANKYOU_PAGE]: {
    ...STAY_ON_ANY_DOMAIN,
    path: '/payment/thankyou/',
    thunk: (request) =>
      import(`./routeThunks/payment/onEnterThankYouPage`).then(
        ({ onEnterThankYouPage }) => onEnterThankYouPage(request)
      ),
  },
  [ROUTE_PAYMENT_OTHER_PAGE]: {
    ...STAY_ON_ANY_DOMAIN,
    path: '/payment/:subPath*/',
  },

  [ROUTE_CERTIFICATE_VERIFICATION_PAGE]: {
    path: '/certificate-verification/',
  },

  // User created page outside course or institution
  [ROUTE_OTHER_PAGE]: {
    path: `/:prefix(${reservedPrefixes.join('|')})/:subPath*/`,
  },

  // Institution homepage
  [ROUTE_INSTITUTION_HOMEPAGE]: {
    domains: [
      {
        domain: MAIN_DOMAIN,
        path: '/:institutionPrefix/',
        duringThunk: getCurrentInstitution,
        makeSelectOriginForAction: makeSelectOlOriginForInstitutionPrefix,
      },
      {
        domain: CUSTOM_DOMAINS,
        path: '/',
        duringThunk: getCurrentInstitution,
        makeSelectOriginForAction: makeSelectCustomOriginFromInstitutionPrefix,
      },
    ],
    injectInstitutionPrefix: true,
  },

  // Qualification page
  [ROUTE_INSTITUTION_QUALIFICATION_PAGE]: {
    domains: [
      {
        domain: CUSTOM_DOMAINS,
        path: '/qualifications/:qualificationCode/',
        duringThunk: getCurrentInstitution,
        makeSelectOriginForAction: makeSelectCustomOriginFromInstitutionPrefix,
      },
      {
        domain: MAIN_DOMAIN,
        path: '/:institutionPrefix/qualifications/:qualificationCode/',
        duringThunk: getCurrentInstitution,
        makeSelectOriginForAction: makeSelectOlOriginForInstitutionPrefix,
      },
    ],
    injectInstitutionPrefix: true,
    thunk: (request) =>
      import(`./routeThunks/institution/onEnterQualificationLandingPage`).then(
        ({ onEnterQualificationLandingPage }) =>
          onEnterQualificationLandingPage(request)
      ),
  },

  // Subscriptions page
  [ROUTE_INSTITUTION_SUBSCRIPTIONS_PAGE]: {
    domains: [
      {
        domain: CUSTOM_DOMAINS,
        path: '/subscriptions/:subPath*/',
        duringThunk: getCurrentInstitution,
        makeSelectOriginForAction: makeSelectCustomOriginFromInstitutionPrefix,
      },
      {
        domain: MAIN_DOMAIN,
        path: '/:institutionPrefix/subscriptions/:subPath+/',
        duringThunk: getCurrentInstitution,
        makeSelectOriginForAction: makeSelectOlOriginForInstitutionPrefix,
      },
    ],
    injectInstitutionPrefix: true,
  },

  // User created page inside an institution but outside any course
  [ROUTE_INSTITUTION_PAGE]: {
    domains: [
      {
        domain: CUSTOM_DOMAINS,
        path: '/:subPath*/',
        makeSelectOriginForAction: makeSelectCustomOriginFromInstitutionPrefix,
      },
      {
        domain: MAIN_DOMAIN,
        path: '/:institutionPrefix/:subPath+/',
        makeSelectOriginForAction: makeSelectOlOriginForInstitutionPrefix,
      },
    ],
    injectInstitutionPrefix: true,
    thunk: getCurrentInstitution,
  },

  NOT_FOUND: {
    /**
     * This is important so that rudy does not crash when we pass
     * our own `routes` object directly into `urlToAction`, and also
     * so that we explicitly specify the path of the 404 page.
     */
    path: '/404/',
  },
};
